import { useState, useEffect, useCallback } from 'react'
import { usersApi, type CrmUser, USER_ROLES } from '../api/users'
import { userCategoriesApi, type UserCategory } from '../api/team'
import {
  Page,
  PageHeader,
  FilterBar,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  Checkbox,
  Pagination,
  useSnackbar,
  type PillTone,
  useConfirm,
  FormSection,
  FormGrid,
} from '../components/ui'

interface UserFormState {
  name: string
  email: string
  password: string
  role: string
  isActive: boolean
  userCategoryId: string
  parentUserId: string
}

const EMPTY_FORM: UserFormState = {
  name: '',
  email: '',
  password: '',
  role: 'EqualAgente',
  isActive: true,
  userCategoryId: '',
  parentUserId: '',
}

function roleBadge(role: string) {
  const adminRoles = ['Administrator', 'EqualAdmin']
  const dirRoles = ['EqualDirettoreGenerale', 'EqualDirettoreCommerciale']
  const tone: PillTone = adminRoles.includes(role) ? 'error' : dirRoles.includes(role) ? 'warning' : 'neutral'
  return <StatusPill tone={tone}>{role.replace('Equal', '')}</StatusPill>
}

function formatDate(s: string | null): string {
  if (!s) return '—'
  return new Date(s).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export function UsersPage() {
  const askConfirm = useConfirm()
  const snackbar = useSnackbar()
  const [items, setItems] = useState<CrmUser[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState<UserFormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const [showPwdModal, setShowPwdModal] = useState(false)
  const [pwdUserId, setPwdUserId] = useState<number | null>(null)
  const [newPwd, setNewPwd] = useState('')

  const [categories, setCategories] = useState<UserCategory[]>([])
  const [allUsers, setAllUsers] = useState<CrmUser[]>([])

  const pageSize = 25

  // TASK-0058: categories/candidate-managers are loaded once for the create/edit form's two new
  // selectors — independent of the paginated/filtered `items` list above (search/role/page apply
  // only to the table), and independent of each other's loading outcome (best-effort: a failure
  // loading one must not block the other or the rest of the page).
  useEffect(() => {
    userCategoriesApi.list().then(setCategories).catch(() => setCategories([]))
    usersApi.list({ size: 200 }).then(res => setAllUsers(res.items)).catch(() => setAllUsers([]))
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const p: { page: number; search?: string; role?: string } = { page }
      if (search) p.search = search
      if (roleFilter) p.role = roleFilter
      const res = await usersApi.list(p)
      setItems(res.items)
      setTotal(res.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento')
    } finally {
      setLoading(false)
    }
  }, [search, roleFilter, page])

  useEffect(() => { void load() }, [load])

  function openCreate() {
    setForm(EMPTY_FORM)
    setEditId(null)
    setSaveError('')
    setShowModal(true)
  }

  function openEdit(u: CrmUser) {
    setForm({
      name: u.name, email: u.email, password: '', role: u.role, isActive: u.isActive,
      userCategoryId: u.userCategoryId !== null ? String(u.userCategoryId) : '',
      parentUserId: u.parentUserId !== null ? String(u.parentUserId) : '',
    })
    setEditId(u.id)
    setSaveError('')
    setShowModal(true)
  }

  function closeModal() {
    setShowModal(false)
    setEditId(null)
    setSaveError('')
  }

  async function handleSave() {
    if (!form.name.trim() || !form.email.trim()) {
      setSaveError('Nome ed email sono obbligatori')
      return
    }
    if (editId === null && !form.password) {
      setSaveError('La password è obbligatoria per i nuovi utenti')
      return
    }
    setSaving(true)
    setSaveError('')
    // TASK-0058: both optional — '' (nothing selected) maps to null, not to an invalid numeric
    // value, so a user with no category/manager stays a valid, first-class case.
    const userCategoryId = form.userCategoryId ? Number(form.userCategoryId) : null
    const parentUserId = form.parentUserId ? Number(form.parentUserId) : null
    try {
      if (editId !== null) {
        await usersApi.update(editId, {
          name: form.name, email: form.email, role: form.role, isActive: form.isActive,
          userCategoryId, parentUserId,
        })
      } else {
        await usersApi.create({
          name: form.name, email: form.email, password: form.password, role: form.role,
          userCategoryId, parentUserId,
        })
      }
      closeModal()
      void load()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Errore salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function handleDeactivate(u: CrmUser) {
    if (!(await askConfirm({ title: 'Disattiva utente', message: `Disattivare l'utente "${u.name}"?`, confirmLabel: 'Disattiva', destructive: true }))) return
    try {
      await usersApi.deactivate(u.id)
      void load()
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    }
  }

  async function handleUnlock(u: CrmUser) {
    try {
      await usersApi.unlock(u.id)
      void load()
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore sblocco', tone: 'error' })
    }
  }

  async function handleResetMfa(u: CrmUser) {
    if (!window.confirm(`Reimpostare il codice di accesso di ${u.name}? Al prossimo accesso dovrà configurarlo di nuovo.`)) return
    try {
      await usersApi.resetMfa(u.id)
      snackbar.show({ message: 'Codice di accesso reimpostato', tone: 'success' })
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore reimpostazione codice di accesso', tone: 'error' })
    }
  }

  async function handleResetPwd() {
    if (!newPwd || pwdUserId === null) return
    try {
      await usersApi.resetPassword(pwdUserId, newPwd)
      setShowPwdModal(false)
      setNewPwd('')
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore reset password', tone: 'error' })
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <Page>
      <PageHeader
        title="Utenti"
        icon="group"
        count={total > 0 ? total : undefined}
        actions={
          <Button variant="filled" icon="person_add" onClick={openCreate}>
            Nuovo utente
          </Button>
        }
      />

      <FilterBar>
        <TextField
          label="Cerca utente"
          leadingIcon="search"
          type="text"
          supportingText="Nome o email..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }}
        />
        <Select
          label="Ruolo"
          value={roleFilter}
          onChange={e => { setRoleFilter(e.target.value); setPage(1) }}
        >
          <option value="">Tutti i ruoli</option>
          {USER_ROLES.map(r => (
            <option key={r} value={r}>{r.replace('Equal', '')}</option>
          ))}
        </Select>
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}

      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <EmptyState icon="group" title="Nessun utente trovato" />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Ruolo</th>
                <th>Stato</th>
                <th>Ultimo login</th>
                <th>Tentativi</th>
                <th className="text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map(u => (
                <tr key={u.id} className={!u.isActive ? 'text-muted' : ''}>
                  <td className="md-typescale-emphasized-body-medium">{u.name}</td>
                  <td className="text-muted md-typescale-body-small">{u.email}</td>
                  <td>{roleBadge(u.role)}</td>
                  <td>
                    {u.isActive
                      ? <StatusPill tone="success">Attivo</StatusPill>
                      : <StatusPill tone="neutral">Inattivo</StatusPill>}
                    {u.lockedUntil && new Date(u.lockedUntil) > new Date() && (
                      <StatusPill tone="error" className="ms-1">Bloccato</StatusPill>
                    )}
                  </td>
                  <td className="text-muted md-typescale-body-small">{formatDate(u.lastLoginAt)}</td>
                  <td className="text-muted md-num">
                    {u.loginAttempts > 0
                      ? <span className="text-warning md-typescale-emphasized-body-medium">{u.loginAttempts}</span>
                      : '0'}
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end">
                      {u.lockedUntil && new Date(u.lockedUntil) > new Date() && (
                        <IconButton icon="lock_open" size="sm" aria-label="Sblocca" onClick={() => void handleUnlock(u)} />
                      )}
                      <IconButton icon="key" size="sm" aria-label="Reset password"
                        onClick={() => { setPwdUserId(u.id); setNewPwd(''); setShowPwdModal(true) }} />
                      <IconButton icon="phonelink_lock" size="sm" aria-label="Reimposta codice di accesso" onClick={() => void handleResetMfa(u)} />
                      <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(u)} />
                      {u.isActive && (
                        <IconButton icon="person_off" size="sm" aria-label="Disattiva" onClick={() => void handleDeactivate(u)} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
        {totalPages > 1 && <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} />}
      </Card>

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={closeModal}
        title={editId !== null ? 'Modifica utente' : 'Nuovo utente'}
        actions={
          <>
            <Button variant="text" onClick={closeModal}>Annulla</Button>
            <Button type="submit" variant="text" disabled={saving} >
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <>
          {saveError && <Callout tone="error">{saveError}</Callout>}
          <FormSection title="Account">
            <TextField label="Nome *" type="text" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <TextField label="Email *" type="email" value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            {editId === null && (
              <TextField label="Password *" type="password" value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            )}
          </FormSection>
          <FormSection title="Ruolo e gerarchia">
            <Select label="Ruolo" value={form.role}
              onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
              {USER_ROLES.map(r => <option key={r} value={r}>{r.replace('Equal', '')}</option>)}
            </Select>
            <Select label="Categoria Utente" value={form.userCategoryId}
              onChange={e => setForm(f => ({ ...f, userCategoryId: e.target.value }))}>
              <option value="">Nessuna categoria</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <Select className="md-span-full" label="Utente Superiore / Responsabile" value={form.parentUserId}
              onChange={e => setForm(f => ({ ...f, parentUserId: e.target.value }))}>
              <option value="">Nessun responsabile</option>
              {allUsers.filter(u => u.id !== editId).map(u => (
                <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
              ))}
            </Select>
          </FormSection>
          {editId !== null && (
            <FormSection title="Opzioni" columns={1}>
              <Checkbox id="chk-active" label="Utente attivo"
                checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
            </FormSection>
          )}
        </>
      </Dialog>

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleResetPwd() }}
        open={showPwdModal}
        onClose={() => setShowPwdModal(false)}
        size="sm"
        title="Reset password"
        actions={
          <>
            <Button variant="text" onClick={() => setShowPwdModal(false)}>Annulla</Button>
            <Button type="submit" variant="text" >Imposta</Button>
          </>
        }
      >
        <FormGrid columns={1}>
          <TextField label="Nuova password" type="password" value={newPwd}
            onChange={e => setNewPwd(e.target.value)} />
        </FormGrid>
      </Dialog>
    </Page>
  )
}
